import React, { useEffect, useState, useMemo } from "react";
import { Col, Row, Tabs } from "antd";
import { useRouter } from "next/router";
import styles from "@aspen/theme/downBill.module.less";
import { i18nUtil, getQueryValue } from "@aspen/libs";
import {
  PortfolioReportTab,
  DocumentAttachmentTab,
  GeneratedHistoryTab,
} from "@aspen/widgets/documentCenter";

// Tab key 类型定义
type TTabKey = "portfolioReport" | "fundStatement" | "financialCompliance" | "generatedHistory";

const DocumentCenter: React.FC = () => {
  const intl = i18nUtil.t();
  const router = useRouter();

  // Tab 相关状态
  const [activeTabKey, setActiveTabKey] = useState<TTabKey | null>("portfolioReport");

  // 初始化Tab状态，根据URL query参数Type字段设置
  useEffect(() => {
    const typeParam = router?.query?.type?.toString() ?? getQueryValue("type");
    if (typeParam) {
      // 验证传入的Type是否为有效的Tab key
      const validTabKeys: TTabKey[] = [
        "portfolioReport",
        "fundStatement",
        "financialCompliance",
        "generatedHistory",
      ];
      if (validTabKeys.includes(typeParam as TTabKey)) {
        const targetKey = typeParam as TTabKey;
        // 只有当前状态与URL参数不一致时才更新状态
        if (activeTabKey !== targetKey) {
          setActiveTabKey(targetKey);
        }
      }
    }
  }, [router?.query?.type]);

  // Tab 切换处理函数
  const handleTabChange = (key: string) => {
    const targetKey = key as TTabKey;
    setActiveTabKey(targetKey);

    // 同步更新URL的Type参数
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("type", targetKey);

    // 使用router.replace来更新URL，不会在浏览器历史中创建新记录
    router.replace(currentUrl.pathname + currentUrl.search, undefined, { shallow: true });
  };

  // 使用useMemo缓存所有Tab组件，避免重复创建
  const tabComponents = useMemo(
    () => ({
      portfolioReport: <PortfolioReportTab />,
      fundStatement: <DocumentAttachmentTab category="fund_statement" />,
      financialCompliance: <DocumentAttachmentTab category="financial_compliance" />,
      generatedHistory: <GeneratedHistoryTab />,
    }),
    [],
  );

  // 获取当前激活的Tab内容
  const tabContent = tabComponents[activeTabKey || "portfolioReport"];

  return (
    <section className={`${styles.bill} ` + `customer`}>
      <div className="customerTop">
        <Row className="top" justify="space-between">
          <Col>
            <p className="title">{intl["document.center.title"]}</p>
          </Col>
        </Row>
      </div>
      <div className={styles.tableWrap}>
        <div className="section" style={{ width: "100%", marginTop: 0 }}>
          <Tabs
            items={[
              { key: "portfolioReport", label: intl["document.center.portfolio.report"] },
              { key: "fundStatement", label: intl["document.center.fund.statement"] },
              { key: "financialCompliance", label: intl["document.center.financial.compliance"] },
              { key: "generatedHistory", label: intl["document.center.generated.history"] },
            ]}
            onChange={handleTabChange}
            className={styles.tab}
            activeKey={activeTabKey || "portfolioReport"}
          />
          {tabContent}
        </div>
      </div>
    </section>
  );
};

export const PageDocumentCenter = React.memo(DocumentCenter);
